package com.sddamico.draftmachine.ds

import com.varabyte.kobweb.compose.ui.graphics.Color

/**
 * Draft Machine — color tokens. Full Kotlin translation of `tokens/colors.css`.
 * "You are in the machine": cool gunmetal iron & polished steel, lit by cartoon
 * metals (copper, silver) and the glow of aether glass.
 *
 * Ramps are the raw palette; the `Semantic` object below aliases them to roles.
 * Prefer the semantic aliases (`DmColor.bg`, `DmColor.primary`, …) in components;
 * reach for a ramp value only when a component needs a specific shade.
 */
object DmColor {
  // ---- Steel & iron neutral ramp (cool gunmetal -> polished silver) ----
  val steel980 = Color.rgb(0x0D1013) // boiler black, deepest sunken
  val steel950 = Color.rgb(0x12161B) // app background — cold iron
  val steel900 = Color.rgb(0x161C22)
  val steel850 = Color.rgb(0x1D242C) // brushed-steel panel
  val steel800 = Color.rgb(0x26303A) // raised panel
  val steel700 = Color.rgb(0x35424E) // hairline borders
  val steel600 = Color.rgb(0x4B5A68) // strong border / rivet seat
  val steel500 = Color.rgb(0x6A7B8A) // disabled metal
  val steel400 = Color.rgb(0x8D9DAC) // muted text on dark
  val steel300 = Color.rgb(0xB0BFCC)
  val steel200 = Color.rgb(0xCFDAE2) // silver edge
  val steel150 = Color.rgb(0xDDE5EB)
  val steel100 = Color.rgb(0xEAEFF3) // polished steel plate / primary text on dark
  val steel50 = Color.rgb(0xF4F7F9) // brightest silver

  // ---- Copper (primary — the working metal: pipes, rivets, actions) ----
  val brass300 = Color.rgb(0xE6B183) // light copper sheen
  val brass400 = Color.rgb(0xD6935C)
  val brass500 = Color.rgb(0xC07A44) // primary copper
  val brass600 = Color.rgb(0x9E5F30)
  val brass700 = Color.rgb(0x7C4924)

  // ---- Aether glass (energy — glowing tubes, "powered / on the clock") ----
  val glass300 = Color.rgb(0x86DCD6)
  val glass400 = Color.rgb(0x4FC4C2)
  val glass500 = Color.rgb(0x2FA6A8) // signature energy cyan
  val glass600 = Color.rgb(0x22838A)
  val glass700 = Color.rgb(0x19646C)

  // ---- Rust / oxide (danger — hot corroded iron) ----
  val rust300 = Color.rgb(0xE28A66)
  val rust400 = Color.rgb(0xD0603A)
  val rust500 = Color.rgb(0xB4472A)
  val rust600 = Color.rgb(0x8E381F)
  val rust700 = Color.rgb(0x6E2C19)

  // ---- Verdigris (success — oxidized-copper patina green) ----
  val verde300 = Color.rgb(0x86C7AB)
  val verde400 = Color.rgb(0x5AA888)
  val verde500 = Color.rgb(0x458C6F)
  val verde600 = Color.rgb(0x356E57)
  val verde700 = Color.rgb(0x285241)

  // ---- Patina blue (rare cool steel-blue accent) ----
  val patina300 = Color.rgb(0x94B6C6)
  val patina400 = Color.rgb(0x61879A)
  val patina500 = Color.rgb(0x4F7486)
  val patina600 = Color.rgb(0x3B5867)

  // ---- Oil slick (deep iridescent darks, used sparingly) ----
  val oilDeep = Color.rgb(0x0F1418)
  val oilSheen = Color.rgb(0x1E2A32)

  // ============================================================
  // SEMANTIC ALIASES — prefer these in component code.
  // ============================================================

  // Backgrounds & surfaces
  val bg = steel950
  val bgSunken = steel980
  val surfacePanel = steel850 // default brushed-steel card
  val surfaceRaised = steel800
  val surfaceHover = steel800
  val surfacePlate = steel100 // polished steel plate (dark text)
  val surfaceInset = steel900

  // Borders & seams
  val border = steel700
  val borderStrong = steel600
  val rivet = steel500

  // Text
  val text = steel100
  val textSecondary = steel300
  val textMuted = steel400
  val textOnPlate = steel900
  val textOnBrass = steel980

  // Brand / interactive — copper is the primary working metal
  val primary = brass500
  val primaryHover = brass400
  val primaryPress = brass600
  val onPrimary = steel980

  // Secondary accent — the cool energy pop
  val accent = glass500
  val accentHover = glass400

  // Energy — glowing glass, marks "powered / on the clock" surfaces
  val energy = glass500

  // Status (semantic)
  val success = verde500
  val successBg = verde700
  val warning = Color.rgb(0xE8C24A) // honey caution — kept distinct from copper
  val warningBg = Color.rgb(0x6E5518)
  val danger = rust500
  val dangerBg = rust700
  val info = glass500
  val infoBg = glass700

  // Sport accent hooks (sport-agnostic app — neutral by default)
  val sportTint = brass500

  // Focus ring
  val focusRing = brass400

  /** Translucent brass wash at [alpha] — the shared "selected surface" background. */
  fun brassTint(alpha: Float): Color = Color.rgba(192, 122, 68, alpha)
}
